/**
 * Where setup keeps answers in progress: this tab's sessionStorage, one key per person and mode
 * (grams.setup.<user>.<mode>). Kept small and apart from setup itself (which loads only when
 * shown), so ending a session can forget every answer without loading setup.
 */
export const SETUP_KEY_PREFIX = 'grams.setup.';

/** The part of Web Storage used here, so tests can pass their own. */
export interface SetupStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Storage that can also list its keys, to forget them all. */
export interface ListedStore extends SetupStore {
  readonly length: number;
  key(index: number): string | null;
}

export function tabStore(): ListedStore | undefined {
  try {
    return (globalThis as { sessionStorage?: ListedStore }).sessionStorage;
  } catch {
    // Reading sessionStorage itself throws where storage is blocked.
    return undefined;
  }
}

/** Per person and mode, so a preview never fills in a real run, and nobody else on this tab sees the answers. */
export function setupKey(uid: string, mode: 'first' | 'again' | 'preview'): string {
  return `${SETUP_KEY_PREFIX}${uid}.${mode}`;
}

/** A session ended (signed out, expired, deleted): nobody's setup answers stay on this tab for the next person. */
export function forgetSetupAnswers(store: ListedStore | undefined = tabStore()): void {
  try {
    if (!store) return;
    const keys: string[] = [];
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key?.startsWith(SETUP_KEY_PREFIX)) keys.push(key);
    }
    for (const key of keys) store.removeItem(key);
  } catch {
    // Blocked storage keeps nothing to forget.
  }
}
